"use client";

import { useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/Card";
import type { ChangeRate, MemberTabType } from "types/BusinessDashboard";
import { CustomizedAxisTick, CustomTooltip, LastDot } from "../chart-ui-kit";
import { DashboardInnerMembersTabNavigation } from "./inner-tab-navigation";

dayjs.locale("ko");

const StyledCard = styled(Card)`
  border-radius: 0;
  border: none;
  box-shadow: none;
  padding: 0;
  gap: 0;
`;

const StyledCardHeader = styled(CardHeader)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  margin-bottom: 12px;
`;

const StyledCardTitle = styled(CardTitle)`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.2px;
  color: #363e52;
  padding: 0;
`;

const StyledCardContent = styled(CardContent)`
  padding: 0;
`;

const ChartFlexRow = styled.div`
  display: flex;
  width: 100%;
  position: relative;
`;

const ScrollableChartWrapper = styled.div`
  width: 100%;
  padding-left: 16px;
  z-index: 1;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChartInner = styled.div<{ $chartWidth: number }>`
  width: ${({ $chartWidth }) => $chartWidth}px;
  min-width: 600px;
  height: 300px;
  z-index: 1;
`;

const YAxisWrapper = styled.div`
  width: 44px;
  height: 300px;
  flex-shrink: 0;
  position: sticky;
  display: flex;
  align-items: flex-start;
  background: #fff;
  right: 0;
  z-index: 0;
  pointer-events: none;
`;

const StyledLineChart = styled(LineChart)`
  & > svg {
    overflow: visible !important;
  }
`;

export const RealChart = ({
  charts,
  chartWidth,
  currentDate,
  currentMembersInnerTab,
}: {
  charts: {
    periodUnitPrice: {
      day: number;
      value: number;
      date: string;
      changeRatePrevMonth: ChangeRate;
    }[];
    countUnitPrice: {
      day: number;
      value: number;
      date: string;
      changeRatePrevMonth: ChangeRate;
    }[];
  };
  chartWidth: number;
  currentDate: dayjs.Dayjs;
  currentMembersInnerTab: MemberTabType;
}) => {
  // const prevInnerTabRef = useRef(currentMembersInnerTab);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  // const handleScrollRef = useRef<((e: Event) => void) | null>(null);

  // 현재 선택된 탭에 따른 차트 데이터를 오늘 날짜까지만 필터링
  const today = dayjs();
  const currentChartData = useMemo(() => {
    const baseData =
      currentMembersInnerTab === "period"
        ? charts.periodUnitPrice
        : charts.countUnitPrice;

    // 현재 월이고 오늘 날짜까지만 필터링
    if (today.isSame(currentDate, "month")) {
      return baseData.filter((item) => item.day <= today.date());
    }

    return baseData;
  }, [
    currentMembersInnerTab,
    charts.periodUnitPrice,
    charts.countUnitPrice,
    currentDate,
    today,
  ]);

  const values = currentChartData.map((d) => d.value);
  const minValue = Math.min(...values, 0);
  const maxValue = Math.max(...values, 0);

  // 최소 범위 보장 (max==min이면 1만큼 추가)
  const safeMax = maxValue === minValue ? maxValue + 1 : maxValue;
  const safeMin = minValue === maxValue ? minValue - 1 : minValue;

  // domain 계산
  const yDomain = [safeMin < 0 ? safeMin * 1.2 : 0, safeMax * 1.2];

  // X축 도메인을 전체 월로 설정 (간격 유지를 위해)
  const xAxisDomain = useMemo(() => {
    const daysInMonth = currentDate.daysInMonth();
    return [1, daysInMonth];
  }, [currentDate]);

  useEffect(() => {
    const scrollToEnd = () => {
      if (!scrollWrapperRef.current || !currentChartData.length) return;

      const currentScrollWrapper = scrollWrapperRef.current;

      // 기존 이벤트 리스너 제거
      // if (handleScrollRef.current) {
      //   currentScrollWrapper.removeEventListener(
      //     "scroll",
      //     handleScrollRef.current
      //   );
      //   handleScrollRef.current = null;
      // }

      const isCurrentMonth = today.isSame(currentDate, "month");
      const totalDataPoints = currentDate.daysInMonth();
      const chartInnerWidth = Math.max(chartWidth, 600);
      const pointWidth = chartInnerWidth / totalDataPoints;

      // 탭이 바뀌었는지 확인
      // const tabChanged = prevInnerTabRef.current !== currentMembersInnerTab;
      // prevInnerTabRef.current = currentMembersInnerTab;

      if (isCurrentMonth) {
        // 현재 월이면 오늘 날짜로 스크롤하되, Y축에 가깝게 위치 (오른쪽으로 치우치게)
        const todayDay = today.date();
        // 오른쪽으로 치우치게 하기 위해 clientWidth의 3/4 정도 여백을 둠
        const scrollPosition =
          (todayDay - 1) * pointWidth -
          (currentScrollWrapper.clientWidth * 3) / 4 +
          pointWidth / 2;
        const maxScrollPosition =
          (todayDay - 1) * pointWidth -
          (currentScrollWrapper.clientWidth * 3) / 4 +
          pointWidth / 2;
        const finalScrollPosition = Math.max(
          0,
          Math.min(scrollPosition, maxScrollPosition)
        );

        currentScrollWrapper.scrollTo({
          left: finalScrollPosition,
          behavior: "smooth",
        });

        // 스크롤 이벤트 리스너로 최대 스크롤 범위 제한 (현재 월에만 적용)
        // const handleScroll = (e: Event) => {
        //   const target = e.target as HTMLDivElement;
        //   if (target.scrollLeft > maxScrollPosition) {
        //     target.scrollLeft = maxScrollPosition;
        //   }
        // };

        // handleScrollRef.current = handleScroll;
        // currentScrollWrapper.addEventListener("scroll", handleScroll);
      } else {
        // 현재 월이 아니면 맨 처음으로 스크롤 (스크롤 제한 없음)
        currentScrollWrapper.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      }
    };

    // 컴포넌트 마운트 후 약간의 지연을 두고 스크롤 (차트 렌더링 완료 대기)
    const timeoutId = setTimeout(scrollToEnd, 100);

    return () => {
      clearTimeout(timeoutId);
      // cleanup 시 이벤트 리스너 제거
      // if (scrollWrapperRef.current && handleScrollRef.current) {
      //   scrollWrapperRef.current.removeEventListener(
      //     "scroll",
      //     handleScrollRef.current
      //   );
      //   handleScrollRef.current = null;
      // }
    };
  }, [
    currentChartData,
    chartWidth,
    currentMembersInnerTab,
    currentDate,
    today,
  ]);

  return (
    <StyledCard>
      <StyledCardHeader>
        <StyledCardTitle>객단가</StyledCardTitle>
      </StyledCardHeader>
      <DashboardInnerMembersTabNavigation />
      <StyledCardContent>
        <ChartFlexRow>
          <ScrollableChartWrapper ref={scrollWrapperRef}>
            <ChartInner $chartWidth={chartWidth}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={currentChartData}
                  margin={{ top: 8, right: 0, left: 0, bottom: 13 }}
                  className="[&_.recharts-surface]:overflow-visible"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#D9DDE5"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="day"
                    type="number"
                    scale="linear"
                    domain={xAxisDomain}
                    tickLine={false}
                    axisLine={{ stroke: "#D9DDE5" }}
                    tick={
                      <CustomizedAxisTick
                        currentYear={currentDate.year()}
                        currentMonth={currentDate.month() + 1}
                      />
                    }
                    interval={0}
                    height={56}
                    tickMargin={13}
                    padding={{
                      left: 16,
                      right: 16,
                    }}
                  />
                  <YAxis
                    width={0}
                    height={0}
                    orientation="right"
                    tickLine={false}
                    axisLine={{ stroke: "#D9DDE5" }}
                    tick={{ fontSize: 12, fontWeight: 500, fill: "#70747D" }}
                    interval={0}
                    tickMargin={0}
                    tickCount={currentMembersInnerTab === "period" ? 5 : 4}
                    domain={yDomain}
                    tickFormatter={(value) =>
                      `${Math.floor(value / 10000).toLocaleString()}`
                    }
                  />
                  <Tooltip
                    trigger="click"
                    content={(props) => (
                      <CustomTooltip
                        {...props}
                        label={
                          currentMembersInnerTab === "period"
                            ? "기간제 객단가"
                            : "횟수제 객단가"
                        }
                      />
                    )}
                    wrapperStyle={{ zIndex: 1 }}
                  />
                  {/* 일요일에만 ReferenceLine을 사용하여 수직선 그리기 */}
                  {charts[
                    currentMembersInnerTab === "period"
                      ? "periodUnitPrice"
                      : "countUnitPrice"
                  ].map((entry, index) => {
                    const chartDate = dayjs(
                      `${currentDate.year()}-${currentDate.month() + 1}-${entry.day}`
                    );
                    const dayOfWeek = chartDate.day();
                    if (dayOfWeek === 0) {
                      return (
                        <ReferenceLine
                          key={`sunday-grid-${index}`}
                          x={entry.day}
                          stroke="#D9DDE5"
                          strokeDasharray="3 3"
                          strokeWidth={1}
                        />
                      );
                    }
                    return null;
                  })}
                  <Line
                    type="linear"
                    dataKey="value"
                    stroke="#7643DB"
                    strokeWidth={3}
                    dot={
                      <LastDot color="#7643DB" chartData={currentChartData} />
                    }
                    activeDot={{ r: 5, fill: "#7643DB" }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartInner>
          </ScrollableChartWrapper>
          <YAxisWrapper>
            <ResponsiveContainer width="100%" height="100%">
              <StyledLineChart
                data={currentChartData}
                margin={{ top: 8, right: -16, left: 0, bottom: 13 }}
              >
                <XAxis
                  dataKey="day"
                  type="number"
                  scale="linear"
                  domain={xAxisDomain}
                  tickLine={false}
                  axisLine={{ stroke: "#D9DDE5" }}
                  tick={
                    <CustomizedAxisTick
                      currentYear={currentDate.year()}
                      currentMonth={currentDate.month() + 1}
                    />
                  }
                  width={0}
                  interval={0}
                  height={56}
                  tickMargin={13}
                  padding={{
                    left: 16,
                    right: 16,
                  }}
                />
                <YAxis
                  orientation="right"
                  tickLine={false}
                  axisLine={{ stroke: "#D9DDE5" }}
                  tick={{ fontSize: 12, fontWeight: 500, fill: "#70747D" }}
                  interval={0}
                  tickMargin={0}
                  tickCount={currentMembersInnerTab === "period" ? 5 : 4}
                  domain={yDomain}
                  tickFormatter={(value) =>
                    `${Math.floor(value / 10000).toLocaleString()}`
                  }
                />
              </StyledLineChart>
            </ResponsiveContainer>
          </YAxisWrapper>
        </ChartFlexRow>
      </StyledCardContent>
    </StyledCard>
  );
};
